import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useUpdate } from "@refinedev/core";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection"
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input"
import { Label } from "components/label"
import Select from "components/select";
import { IOrderDetail, IOrderVATForm } from "interfaces"
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { PHONE_CODE, REGEX_PHONE_NUMBER, REGEX_VALID_EMAIL } from "utills/constants";
import { z } from "zod";

const EditVATSchema = z.object({
  company_name: z.string().min(1, "This field is required"),
  company_address: z.string().min(1, "This field is required"),
  company_tax_code: z.string().min(1, "This field is required"),
  recipient_name: z.string().min(1, "This field is required"),
  recipient_address: z
    .string()
    .min(1, { message: "This field is required" })
    .refine((value) => 
      REGEX_VALID_EMAIL.test(value ?? ""), 
      'Invalid email address'
    ),
  recipient_phone: 
    z.string().min(1, "This field is required")
    .refine((value) => 
      REGEX_PHONE_NUMBER.test(value ?? ""), 
      'Invalid phone number'
    ),
  recipient_phone_area_code: z.string().min(1, "This field is required"),
});

export const EditVATSection = ({orderDetailData, refetch} : {orderDetailData: IOrderDetail, refetch: () => void}) => {
  const { mutate: updateMutation, isLoading: isLoading } = useUpdate();
  const [isShowVATForm, setIsShowVATForm] = useState(orderDetailData?.is_vat ? true: false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IOrderVATForm, HttpError, IOrderVATForm>({
    defaultValues: {
      company_name: orderDetailData?.tax?.company_name ?? "",
      company_address: orderDetailData?.tax?.company_address ?? "",
      company_tax_code: orderDetailData?.tax?.company_tax_code ?? "",
      recipient_name: orderDetailData?.tax?.recipient_name ?? "",
      recipient_address: orderDetailData?.tax?.recipient_address ?? "",
      recipient_phone: orderDetailData?.tax?.recipient_phone ?? "",
      recipient_phone_area_code: orderDetailData?.tax?.recipient_phone_area_code ?? "84",
    },
    resolver: zodResolver(EditVATSchema),
  });

  useEffect(() => {
    if (!isShowVATForm) {
      reset({
        company_name: orderDetailData?.tax?.company_name ?? "",
        company_address: orderDetailData?.tax?.company_address ?? "",
        company_tax_code: orderDetailData?.tax?.company_tax_code ?? "",
        recipient_name: orderDetailData?.tax?.recipient_name ?? "",
        recipient_address: orderDetailData?.tax?.recipient_address ?? "",
        recipient_phone: orderDetailData?.tax?.recipient_phone ?? "",
        recipient_phone_area_code: orderDetailData?.tax?.recipient_phone_area_code ?? "84",
      });  
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowVATForm])

  const onSubmit = (values: IOrderVATForm) => {
    updateMutation(
      {
        resource: "checkout/orders",
        id: `${orderDetailData?.code}/tax`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...values,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          refetch();
        },
      }
    );
  };
  
  
  const _toggleShowVATForm = () => {
    setIsShowVATForm(!isShowVATForm);
  }

  return (
    <>
      <CollapsibleSection 
        hideArrow={orderDetailData?.is_vat ? false: true}
        label="VAT Information"
        customOpen={{
          open: isShowVATForm,
          onClick: () => orderDetailData?.is_vat ? _toggleShowVATForm() : {}
        }}
        toolbar={!orderDetailData?.is_vat ? (
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => {
              e.stopPropagation();
              _toggleShowVATForm();
            }}
          >
            <span className="sr-only">History Log</span>
            {!isShowVATForm ? (
              <PlusCircleIcon className="w-4 h-4 text-brand-500" />
            ) : (
              <TrashIcon className="w-4 h-4 text-brand-500" />
            )}
            <span className="hidden sm:block sm:ml-2">{!isShowVATForm ? "Add" : "Cancel"}</span>
          </Button>
        ) : <></>}
      >
        {isShowVATForm ? (
          <div className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 sm:grid-cols-6 lg:gap-x-6" style={{wordBreak: "break-word"}}>
            <div className="sm:col-span-3 sm:order-1">
              <Label required id="company_name" name="Company name" />
              <Input
                id="company_name"
                placeholder="Company name"
                error={errors["company_name"]}
                {...register("company_name")}
              />
              {errors["company_name"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.company_name.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3 sm:order-3">
              <Label required id="company_address" name="Company address" />
              <Input
                id="company_address"
                placeholder="Company address"
                error={errors["company_address"]}
                {...register("company_address")}
              />
              {errors["company_address"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.company_address.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3 sm:order-5">
              <Label required id="company_tax_code" name="Company tax code" />
              <Input
                id="company_tax_code"
                placeholder="Company tax code"
                error={errors["company_tax_code"]}
                {...register("company_tax_code")}
              />
              {errors["company_tax_code"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.company_tax_code.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3 sm:order-2">
              <Label required id="recipient_name" name="Recipient's name" />
              <Input
                id="recipient_name"
                placeholder="Recipient's name"
                error={errors["recipient_name"]}
                {...register("recipient_name")}
              />
              {errors["recipient_name"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.recipient_name.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3 sm:order-4">
              <Label 
                required
                id="recipient_phone" 
                name="Recipient's phone"
              />
              <div className="flex">
                <Controller
                  control={control}
                  name="recipient_phone_area_code"
                  render={({ field: { onChange, value = "", ref } }) => {
                    const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                    return (
                      <Select 
                        ref={ref}
                        className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                        options={areaPhoneCodeArr} 
                        id="recipient_phone_area_code"
                        value={areaPhoneCodeArr.find(c => c.value === value)}
                        onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                        error={errors["recipient_phone_area_code"]}
                      />
                    )
                  }}
                />
                <Input
                  id="recipient_phone"
                  className={`${errors["recipient_phone"] ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                  placeholder="Phone number"
                  error={errors["recipient_phone"]}
                  {...register("recipient_phone")}
                />
              </div>
              {errors["recipient_phone"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["recipient_phone"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3 sm:order-6">
              <Label required id="recipient_address" name="Email" />
              <Input
                id="recipient_address"
                placeholder="Email"
                error={errors["recipient_address"]}
                {...register("recipient_address")}
              />
              {errors["recipient_address"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.recipient_address.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full overflow-hidden order-last">
              <Button
                type="submit"
                className="w-full sm:w-32 h-11 float-right"
                onClick={handleSubmit(onSubmit)}
              >
                SAVE
              </Button>
            </div>
          </div>
        ) : <></>}
      </CollapsibleSection>
      {isLoading ? <FullScreenLoading /> : <></>}
    </>
  )
}